/* =========================================================================
   ClueVine — Thorn (trace-a-path word hunt, "Boggle")
   Trace a path through touching letters — across, down, AND around corners,
   reusing no cell — to spell as many words as you can. Distinct from Bramble's
   straight-line search: a Thorn path can twist. Grids are rolled from the
   classic 16-die set and then solved by DFS (prefix-pruned against the
   dictionary), so the target is always reachable.
   ========================================================================= */

import type {
  Dictionary,
  DifficultyLevel,
  GenerateContext,
  HintOutcome,
  InputKey,
  MoveFeedback,
  ModeScore,
  Prng,
  PuzzleStatus,
  ResultSummary,
} from '../../engine/types';

export interface ThornConfig {
  minLength: number;
  targetFraction: number;
}

export interface ThornState {
  size: number;
  grid: string[]; // size*size letters
  minLength: number;
  validWords: string[]; // every findable word (sorted)
  paths: Record<string, number[]>; // word -> a winning path (for hints)
  found: string[]; // newest first
  path: number[]; // current trace (cell indices)
  target: number;
  maxPoints: number;
  finished: boolean;
}

export type ThornMove =
  | { type: 'tap'; index: number }
  | { type: 'submit' }
  | { type: 'clear' }
  | { type: 'finish' };

export const THORN_DIFFICULTIES: DifficultyLevel<ThornConfig>[] = [
  { id: 'easy', label: 'Sprout', blurb: '3+ letters · gentle target', scoreMultiplier: 0.9, config: { minLength: 3, targetFraction: 0.18 } },
  { id: 'standard', label: 'Garden', blurb: '3+ letters', scoreMultiplier: 1, config: { minLength: 3, targetFraction: 0.28 } },
  { id: 'hard', label: 'Thicket', blurb: '4+ letters', scoreMultiplier: 1.3, config: { minLength: 4, targetFraction: 0.38 } },
];

export const THORN_RANKS = ['Seed', 'Sprout', 'Sapling', 'Thicket', 'Grove', 'Canopy', 'Wildwood'] as const;

// Classic 16-die Boggle set (Q→R so every die yields usable letters).
const DICE = [
  'aaeegn', 'abbjoo', 'achops', 'affkps', 'aoottw', 'cimotu', 'deilrx', 'delrvy',
  'distty', 'eeghnw', 'eeinsu', 'ehrtvw', 'eiosst', 'elrtty', 'himnru', 'hlnnrz',
];

const MAX_WORD = 9;

export function neighbors(index: number, size: number): number[] {
  const r = Math.floor(index / size);
  const c = index % size;
  const out: number[] = [];
  for (let dr = -1; dr <= 1; dr++) {
    for (let dc = -1; dc <= 1; dc++) {
      if (dr === 0 && dc === 0) continue;
      const nr = r + dr;
      const nc = c + dc;
      if (nr >= 0 && nr < size && nc >= 0 && nc < size) out.push(nr * size + nc);
    }
  }
  return out;
}

export function areAdjacent(a: number, b: number, size: number): boolean {
  return neighbors(a, size).includes(b);
}

export function pathWord(state: ThornState): string {
  return state.path.map((i) => state.grid[i]).join('');
}

export function wordPoints(word: string): number {
  const n = word.length;
  if (n <= 4) return 1;
  if (n === 5) return 2;
  if (n === 6) return 3;
  if (n === 7) return 5;
  return 11;
}

/** DFS every distinct word findable on the grid (prefix-pruned). */
export function solveGrid(
  grid: string[],
  size: number,
  minLength: number,
  dict: Dictionary,
): Record<string, number[]> {
  const found: Record<string, number[]> = {};
  const visited = new Array<boolean>(size * size).fill(false);

  const walk = (idx: number, prefix: string, path: number[]) => {
    const word = prefix + grid[idx];
    if (!dict.hasPrefix(word)) return;
    const next = [...path, idx];
    if (word.length >= minLength && word.length <= MAX_WORD && dict.isValid(word) && !found[word]) {
      found[word] = next;
    }
    if (word.length >= MAX_WORD) return;
    visited[idx] = true;
    for (const nb of neighbors(idx, size)) {
      if (!visited[nb]) walk(nb, word, next);
    }
    visited[idx] = false;
  };

  for (let i = 0; i < grid.length; i++) walk(i, '', []);
  return found;
}

function rollGrid(prng: Prng): string[] {
  const order = prng.shuffle(DICE);
  return order.map((die) => prng.pick(die.split('')));
}

export function generate(ctx: GenerateContext<ThornConfig>): ThornState {
  const { minLength, targetFraction } = ctx.difficulty.config;
  const size = 4;
  const want = minLength >= 4 ? 9 : 16;

  let grid = rollGrid(ctx.prng);
  let paths = solveGrid(grid, size, minLength, ctx.dictionary);
  let count = Object.keys(paths).length;
  let attempts = 0;
  while (count < want && attempts < 60) {
    const g = rollGrid(ctx.prng);
    const p = solveGrid(g, size, minLength, ctx.dictionary);
    const c = Object.keys(p).length;
    if (c > count) {
      grid = g;
      paths = p;
      count = c;
    }
    attempts += 1;
  }

  const validWords = Object.keys(paths).sort();
  const maxPoints = validWords.reduce((sum, w) => sum + wordPoints(w), 0);
  const target = Math.max(1, Math.min(validWords.length, 18, Math.ceil(validWords.length * targetFraction)));

  return { size, grid, minLength, validWords, paths, found: [], path: [], target, maxPoints, finished: false };
}

export function status(state: ThornState): PuzzleStatus {
  const allFound = state.found.length >= state.validWords.length && state.validWords.length > 0;
  if (state.finished || allFound) return state.found.length > 0 ? 'won' : 'lost';
  return 'playing';
}

export function validateMove(state: ThornState, move: ThornMove): { ok: boolean; reason?: string } {
  if (move.type !== 'submit') return { ok: true };
  const word = pathWord(state);
  if (word.length < state.minLength) return { ok: false, reason: `At least ${state.minLength} letters` };
  if (state.found.includes(word)) return { ok: false, reason: 'Already found' };
  if (!state.validWords.includes(word)) return { ok: false, reason: 'Not a word here' };
  return { ok: true };
}

export function reduce(state: ThornState, move: ThornMove): ThornState {
  switch (move.type) {
    case 'tap': {
      const i = move.index;
      if (i < 0 || i >= state.grid.length) return state;
      if (state.path.length === 0) return { ...state, path: [i] };
      const last = state.path[state.path.length - 1];
      if (i === last) {
        // tap the tail again to confirm/submit
        return submitPath(state);
      }
      const existingAt = state.path.indexOf(i);
      if (existingAt >= 0) {
        // tap an earlier cell: trim back to it
        return { ...state, path: state.path.slice(0, existingAt + 1) };
      }
      if (areAdjacent(last, i, state.size)) return { ...state, path: [...state.path, i] };
      // not adjacent: start a fresh trace
      return { ...state, path: [i] };
    }
    case 'submit':
      return submitPath(state);
    case 'clear':
      return state.path.length ? { ...state, path: [] } : state;
    case 'finish':
      return { ...state, finished: true };
    default:
      return state;
  }
}

function submitPath(state: ThornState): ThornState {
  const word = pathWord(state);
  if (word.length < state.minLength || state.found.includes(word) || !state.validWords.includes(word)) {
    return { ...state, path: [] };
  }
  return { ...state, found: [word, ...state.found], path: [] };
}

export function moveFeedback(prev: ThornState, next: ThornState, move: ThornMove): MoveFeedback | null {
  if (next.found.length > prev.found.length) {
    const word = next.found[0];
    return { message: `${word.toUpperCase()} +${wordPoints(word)}`, tone: 'good' };
  }
  const banked =
    move.type === 'submit' || (move.type === 'tap' && prev.path[prev.path.length - 1] === move.index);
  if (banked && prev.path.length > 0 && next.path.length === 0) {
    const word = pathWord(prev);
    if (word.length < prev.minLength) return { message: `At least ${prev.minLength} letters`, tone: 'error' };
    if (prev.found.includes(word)) return { message: 'Already found', tone: 'error' };
    return { message: `${word.toUpperCase()} isn’t a word here`, tone: 'error' };
  }
  return null;
}

export function handleKey(state: ThornState, key: InputKey): ThornMove | null {
  if (key.type === 'enter') return state.path.length ? { type: 'submit' } : null;
  if (key.type === 'backspace') return state.path.length ? { type: 'clear' } : null;
  return null;
}

export function hint(state: ThornState): HintOutcome<ThornState> | null {
  if (status(state) !== 'playing') return null;
  const remaining = state.validWords.filter((w) => !state.found.includes(w));
  if (remaining.length === 0) return null;
  const target = [...remaining].sort((a, b) => a.length - b.length)[0];
  return {
    message: `Try a ${target.length}-letter word starting with “${target[0].toUpperCase()}”.`,
    penalty: 40,
  };
}

export function foundPoints(state: ThornState): number {
  return state.found.reduce((sum, w) => sum + wordPoints(w), 0);
}

export function thornRank(state: ThornState): { label: string; index: number; fraction: number } {
  const fraction = state.maxPoints > 0 ? foundPoints(state) / state.maxPoints : 0;
  const index = Math.min(THORN_RANKS.length - 1, Math.floor(fraction * THORN_RANKS.length));
  return { label: THORN_RANKS[index], index, fraction };
}

export function score(state: ThornState): ModeScore {
  const base = foundPoints(state) * 14;
  return {
    base,
    lines: [{ label: `${state.found.length} words traced`, value: base, kind: 'base' }],
    parMs: state.target * 12000,
    perfect: state.validWords.length > 0 && state.found.length === state.validWords.length,
  };
}

export function summarize(state: ThornState): ResultSummary {
  const won = status(state) === 'won';
  const longest = state.found.reduce((a, b) => (b.length > a.length ? b : a), '');
  return {
    status: won ? 'won' : 'lost',
    headline: won ? `${thornRank(state).label} · ${state.found.length} words` : 'No words traced',
    detail: longest ? `Longest: ${longest.toUpperCase()}` : undefined,
    revealLabel: 'Best find',
    shareGrid: [`🌳 ${state.found.length}/${state.validWords.length} words`],
  };
}

export function serialize(state: ThornState): unknown {
  return state;
}

export function deserialize(raw: unknown): ThornState | null {
  if (!raw || typeof raw !== 'object') return null;
  const s = raw as Partial<ThornState>;
  if (typeof s.size !== 'number' || !Array.isArray(s.grid) || !Array.isArray(s.validWords)) return null;
  if (!s.paths || typeof s.paths !== 'object' || !Array.isArray(s.found)) return null;
  return {
    size: s.size,
    grid: s.grid.filter((c): c is string => typeof c === 'string'),
    minLength: typeof s.minLength === 'number' ? s.minLength : 3,
    validWords: s.validWords.filter((w): w is string => typeof w === 'string'),
    paths: s.paths as Record<string, number[]>,
    found: s.found.filter((w): w is string => typeof w === 'string'),
    path: Array.isArray(s.path) ? s.path.filter((n): n is number => typeof n === 'number') : [],
    target: typeof s.target === 'number' ? s.target : 1,
    maxPoints: typeof s.maxPoints === 'number' ? s.maxPoints : 0,
    finished: Boolean(s.finished),
  };
}
